import React, { useEffect, useRef, useState, useCallback, memo } from 'react';
import styled from 'styled-components';

const PaymentContainer = styled.div`
  width: 100%;
  max-width: 1000px;
  margin: 0 auto;
  padding: 0;
  min-height: auto;
  border-radius: 0;
  box-shadow: none;
  background: transparent;
  transition: all 0.3s ease-in-out;
  display: block;
  border: none;
  position: relative;
  
  @media (max-width: 768px) {
    padding: 0;
    max-width: 100%;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  color: #666;
  
  .spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #f3f3f3;
    border-top: 4px solid #007bff;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 1rem;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorContainer = styled.div`
  background: #fff3cd;
  border: 3px solid #ffc107;
  border-radius: 12px;
  padding: 1.5rem;
  margin: 1.5rem 0;
  box-shadow: 0 4px 12px rgba(220, 53, 69, 0.15);
  animation: shake 0.5s ease-in-out;
  
  @keyframes shake {
    0%, 100% { transform: translateX(0); }
    25% { transform: translateX(-10px); }
    75% { transform: translateX(10px); }
  }
`;

const ErrorTitle = styled.div`
  font-size: 1.1rem;
  font-weight: bold;
  color: #856404;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ErrorMessage = styled.div`
  color: #856404;
  font-size: 1rem;
  line-height: 1.6;
`;

const RetryButton = styled.button`
  background: linear-gradient(135deg, #9d6d49 0%, #856044 100%);
  color: white;
  border: none;
  padding: 0.875rem 1.5rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  margin-top: 1rem;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(157, 109, 73, 0.3);
  
  &:hover {
    background: linear-gradient(135deg, #856044 0%, #6d4d37 100%);
    box-shadow: 0 4px 12px rgba(157, 109, 73, 0.4);
    transform: translateY(-2px);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const PaymentSenseConnectE = ({
  paymentSession,
  onPaymentProcessing,
  onPaymentSuccess,
  onPaymentFailure,
  onPaymentCancel
}) => {
  // Log when component receives new props
  console.log('🔄 [PaymentSense] Component rendered/updated with props:', {
    hasPaymentSession: !!paymentSession,
    hasJWT: !!paymentSession?.paymentSenseJWT,
    hasCheckoutUrl: !!paymentSession?.paymentSenseCheckoutUrl
  });

  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [isPaymentFormReady, setIsPaymentFormReady] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [error, setError] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [retryKey, setRetryKey] = useState(0); // For forcing re-render
  const paymentFormRef = useRef(null);
  const connectERef = useRef(null);
  
  // Function to reinitialize payment form
  const handleRetryPayment = () => {

    // Clear error state
    setError(null);
    setProcessingPayment(false);
    setIsPaymentFormReady(false);
    
    // Clean up existing Connect-E instance
    if (connectERef.current) {
      try {
        connectERef.current = null;
      } catch (e) {

      }
    }
    
    // Clear the payment form container
    const container = document.getElementById('payment-form-container');
    if (container) {
      container.innerHTML = '';

    }
    
    // Increment retry key to force component re-render
    setRetryKey(prev => prev + 1);

    // Prefer delegating to parent to reset flow (navigate back to review/discounts)
    if (typeof onPaymentCancel === 'function') {
      try {
        onPaymentCancel({ reason: 'retry' });
      } catch (e) {

      }
    }
  };

  // Load Payment Sense script only
  useEffect(() => {
    console.log('📜 [PaymentSense] Script loading useEffect triggered', {
      hasPaymentSession: !!paymentSession,
      checkoutUrl: paymentSession?.paymentSenseCheckoutUrl
    });

    const scriptUrl = paymentSession?.paymentSenseCheckoutUrl;

    if (!scriptUrl) {
      console.log('⏸️ [PaymentSense] No checkout URL available yet');
      return;
    }

    const scriptId = 'paymentsense-connect-e';

    // Check if script is already loaded
    const existingScript = document.getElementById(scriptId);
    if (existingScript) {
      console.log('✅ [PaymentSense] Script already exists in DOM');
      // Check if Connect-E is available
      if (window.Connect && window.Connect.ConnectE) {
        console.log('✅ [PaymentSense] Connect.ConnectE already available');
        setIsScriptLoaded(true);
      } else {
        console.log('⏳ [PaymentSense] Script exists but Connect.ConnectE not ready, waiting...');
        // Wait for it to become available
        let attempts = 0;
        const maxAttempts = 30;
        const checkInterval = setInterval(() => {
          attempts++;
          if (window.Connect && window.Connect.ConnectE) {
            console.log('✅ [PaymentSense] Connect.ConnectE now available');
            clearInterval(checkInterval);
            setIsScriptLoaded(true);
          } else if (attempts >= maxAttempts) {
            console.error('❌ [PaymentSense] Timeout waiting for Connect.ConnectE');
            clearInterval(checkInterval);
            setError('Payment form initialization timeout. Please refresh and try again.');
          }
        }, 100);
      }
      return;
    }

    console.log('📥 [PaymentSense] Loading Payment Sense script');

    const script = document.createElement('script');
    script.id = scriptId;
    script.src = scriptUrl;
    script.async = true;

    script.onload = () => {
      console.log('✅ [PaymentSense] Script loaded successfully');

      // Wait for Connect.ConnectE to be available (script execution may take a moment)
      let attempts = 0;
      const maxAttempts = 50; // 5 seconds maximum wait (50 * 100ms)

      const checkConnectEReady = () => {
        attempts++;
        console.log(`⏳ [PaymentSense] Checking for Connect.ConnectE... attempt ${attempts}/${maxAttempts}`);

        if (window.Connect && window.Connect.ConnectE) {
          console.log('✅ [PaymentSense] Connect.ConnectE is ready!');
          setIsScriptLoaded(true);
        } else if (attempts >= maxAttempts) {
          console.error('❌ [PaymentSense] Timeout waiting for Connect.ConnectE');
          setError('Payment form initialization timeout. Please refresh and try again.');
        } else {
          console.log('⏳ [PaymentSense] Connect.ConnectE not ready yet, checking again...');
          setTimeout(checkConnectEReady, 100);
        }
      };

      // Start checking after a small delay
      setTimeout(checkConnectEReady, 50);
    };

    script.onerror = (e) => {
      console.error('❌ [PaymentSense] Script loading error:', e);
      setError('Failed to load payment form. Please refresh and try again.');
    };

    document.head.appendChild(script);
    console.log('📜 [PaymentSense] Script tag added to document.head');

    // Cleanup on unmount - but only remove script, don't clean up on every re-render
    return () => {
      // Don't remove the script - let it persist for the session
      // Script removal was causing double initialization issues
    };
  }, [paymentSession?.paymentSenseCheckoutUrl]);

  // Initialize Payment Sense Connect-E Checkout
  useEffect(() => {
    let isMounted = true;
    let initTimeout = null;

    const initializePaymentForm = async () => {
      console.log('🚀 [PaymentSense] initializePaymentForm called', {
        isScriptLoaded,
        hasPaymentSession: !!paymentSession,
        hasConnectEInstance: !!connectERef.current,
        retryKey
      });

      if (!isScriptLoaded || !paymentSession) {
        console.log('⏸️ [PaymentSense] Not ready yet - waiting for script or payment session');
        return;
      }

      // Prevent re-initialization if already initialized
      if (connectERef.current) {
        console.log('⏸️ [PaymentSense] Already initialized, skipping');
        return;
      }

      // Only allow Payment Sense
      const shouldProceedWithPaymentSense = paymentSession.paymentSenseCheckoutUrl && window.Connect && window.Connect.ConnectE;
      
      if (!shouldProceedWithPaymentSense) {
        console.log('⏸️ [PaymentSense] Payment Sense not ready yet', {
          hasCheckoutUrl: !!paymentSession.paymentSenseCheckoutUrl,
          hasConnect: !!window.Connect,
          hasConnectE: !!window.Connect?.ConnectE
        });
        return;
      }

      // Wait for DOM to be fully ready before initializing
      // Use requestAnimationFrame to ensure React has committed DOM changes
      console.log('⏳ [PaymentSense] Waiting for DOM to be ready...');
      
      await new Promise(resolve => {
        // Use RAF + setTimeout for better reliability
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            initTimeout = setTimeout(resolve, 100);
          });
        });
      });

      if (!isMounted) {
        console.log('⏸️ [PaymentSense] Component unmounted during wait');
        return;
      }

      // Poll for container readiness (up to 2 seconds)
      let container = null;
      const maxAttempts = 20;
      let attempts = 0;
      
      while (!container && attempts < maxAttempts && isMounted) {
        container = document.getElementById('payment-form-container');
        if (!container) {
          console.log(`⏳ [PaymentSense] Container not found yet, attempt ${attempts + 1}/${maxAttempts}`);
          await new Promise(resolve => setTimeout(resolve, 100));
          attempts++;
        } else {
          // Found it! But wait one more tick to ensure it's fully rendered
          await new Promise(resolve => setTimeout(resolve, 50));
        }
      }

      if (!container) {
        console.error('❌ [PaymentSense] Container not found after polling');
        if (isMounted) {
          setError('Payment form container not found. Please refresh.');
        }
        return;
      }

      console.log('✅ [PaymentSense] Container ready after', attempts, 'attempts. Proceeding with initialization', {
        containerExists: true,
        containerHasChildren: container.children.length > 0,
        containerClientHeight: container.clientHeight,
        containerOffsetHeight: container.offsetHeight
      });

      try {
        if (paymentSession.paymentSenseCheckoutUrl && window.Connect && window.Connect.ConnectE) {

          const displayErrors = (errors) => {
            if (errors && errors.length) {

            }
          };

          // Connect-E Standard per official docs: exact copy of working example
          const config = {
            paymentDetails: {
              paymentToken: paymentSession.paymentSenseJWT
            },
            containerId: 'payment-form-container',
            fontCss: ['https://fonts.googleapis.com/css?family=Inter'],
            redirectOnSuccess: false, // Disable automatic redirect on success
            // 3D Secure configuration - critical for handling authentication challenges
            threeDS: {
              enabled: true,
              challengeWindowSize: '05', // Full-page challenge (600x400px minimum)
              challengePreference: 'NO_PREFERENCE',
              timeout: 600, // 10 minutes timeout for 3DS authentication (prevent polling exceeded)
            },
          styles: {
            base: {
              default: {
                color: "#2c3e50",
                textDecoration: "none",
                fontFamily: "'Inter', sans-serif",
                boxSizing: "border-box",
                padding: "0.875rem 1rem",
                boxShadow: 'none',
                fontSize: '1rem',
                borderRadius: '12px',
                lineHeight: '1.5',
                backgroundColor: '#fff',
                border: '2px solid #e9ecef'
              },
              focus: {
                color: '#2c3e50',
                borderColor: '#007bff',
                boxShadow: '0 0 0 3px rgba(0, 123, 255, 0.25)'
              },
              error: {
                color: "#dc3545",
                borderColor: "#dc3545"
              },
              valid: {
                color: "#28a745",
                borderColor: '#28a745'
              },
              label: {
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: '700',
                color: '#2c3e50',
                fontSize: '0.85rem',
                textTransform: 'uppercase',
                letterSpacing: '0.03em'
              }
            },
            cv2: {
              container: {
                width: "35%",
                boxSizing: "border-box"
              },
              default: {
                borderRadius: "12px",
                border: "2px solid #e9ecef",
                width: "100%"
              }
            },
            expiryDate: {
              container: {
                width: "60%",
                boxSizing: "border-box"
              },
              default: {
                borderRadius: "12px",
                border: "2px solid #e9ecef",
                width: "100%"
              },
            },
            cardNumber: {
              container: {
                width: "100%",
                marginBottom: "1rem"
              },
              default: {
                borderRadius: "12px",
                border: "2px solid #e9ecef",
                width: "100%"
              },
            }
          }
        };

        // Container existence already checked above, skip re-check
        
        // Clean the container before initialization to prevent conflicts
        // Only skip initialization if we already have a valid connectE instance
        if (container.children.length > 0 && connectERef.current) {

          if (isMounted) {
            setIsPaymentFormReady(true);
          }
          return;
        }
        
        // Clear container if it has stale content but no valid instance
        if (container.children.length > 0 && !connectERef.current) {
          console.log('🧹 [PaymentSense] Clearing stale container content');
          container.innerHTML = '';
        }

        const onBlur = (isValid) => {
          setIsFormValid(!!isValid);
        };
        
        // 3D Secure event handlers - critical for handling authentication challenges
        const on3DSReady = () => {
          console.log('🔐 [PaymentSense] 3D Secure ready - challenge should appear now!');
          console.log('🔐 [PaymentSense] If you don\'t see a popup, check if it\'s blocked by browser');
        };
        
        const on3DSSuccess = (result) => {
          console.log('✅ [PaymentSense] 3D Secure authentication successful');
        };
        
        const on3DSFailure = (error) => {
          console.error('❌ [PaymentSense] 3D Secure authentication failed');
          setError('3D Secure authentication failed. Please try again or use a different card.');
          setProcessingPayment(false);
          if (onPaymentProcessing) onPaymentProcessing(false);
        };
        
        // Additional 3DS debugging
        const on3DSChallengePresented = () => {
          console.log('🎯 [PaymentSense] 3DS CHALLENGE PRESENTED - popup should be visible!');
        };
        
        try {
          console.log('🔧 [PaymentSense] 3DS Config:', {
            enabled: config.threeDS.enabled,
            timeout: config.threeDS.timeout,
            challengeWindowSize: config.threeDS.challengeWindowSize
          });
          
          // Initialize Connect-E exactly as per official documentation
          const instance = new window.Connect.ConnectE(config, displayErrors, on3DSReady);
          connectERef.current = instance;
          
          // Register 3DS event listeners if available
          if (instance.on) {
            console.log('✅ [PaymentSense] Registering 3DS event listeners');
            instance.on('3DS_SUCCESS', on3DSSuccess);
            instance.on('3DS_FAILURE', on3DSFailure);
            instance.on('3DS_CHALLENGE_PRESENTED', on3DSChallengePresented);
          } else {
            console.warn('⚠️ [PaymentSense] Event listeners not available on instance');
            console.log('🔍 [PaymentSense] Setting up DOM observer for 3DS iframe detection');
            
            // Since event listeners aren't available, aggressively monitor DOM for 3DS iframes
            const observer = new MutationObserver((mutations) => {
              // Look for any new iframes that might be 3DS challenges
              const allIframes = document.querySelectorAll('iframe');
              allIframes.forEach(iframe => {
                const name = (iframe.name || '').toLowerCase();
                const id = (iframe.id || '').toLowerCase();
                const src = (iframe.src || '').toLowerCase();
                
                // Check if this looks like a 3DS iframe
                if (name.includes('threeds') || name.includes('3ds') || name.includes('cardinal') ||
                    id.includes('threeds') || id.includes('3ds') || id.includes('cardinal') ||
                    src.includes('cardinalcommerce') || src.includes('3ds') || src.includes('threeds')) {
                  
                  console.log('🎯 [PaymentSense] 3DS iframe detected!', { name, id, src: iframe.src });
                  
                  // Force it to be visible and centered
                  iframe.style.setProperty('position', 'fixed', 'important');
                  iframe.style.setProperty('top', '50%', 'important');
                  iframe.style.setProperty('left', '50%', 'important');
                  iframe.style.setProperty('transform', 'translate(-50%, -50%)', 'important');
                  iframe.style.setProperty('width', '600px', 'important');
                  iframe.style.setProperty('height', '600px', 'important');
                  iframe.style.setProperty('max-width', '95vw', 'important');
                  iframe.style.setProperty('max-height', '95vh', 'important');
                  iframe.style.setProperty('z-index', '999999', 'important');
                  iframe.style.setProperty('background', 'white', 'important');
                  iframe.style.setProperty('border', 'none', 'important');
                  iframe.style.setProperty('box-shadow', '0 0 0 9999px rgba(0, 0, 0, 0.8)', 'important');
                  iframe.style.setProperty('border-radius', '12px', 'important');
                  iframe.style.setProperty('display', 'block', 'important');
                  iframe.style.setProperty('visibility', 'visible', 'important');
                  iframe.style.setProperty('opacity', '1', 'important');
                  
                  console.log('✅ [PaymentSense] 3DS iframe styling applied!');
                }
              });
            });
            
            // Start observing the entire document for iframe additions
            observer.observe(document.body, {
              childList: true,
              subtree: true,
              attributes: true,
              attributeFilter: ['style', 'class']
            });
            
            // Store observer for cleanup
            connectERef.current._observer = observer;
          }
          
          if (!isMounted) return;
          
          setIsPaymentFormReady(true);

        } catch (initError) {
          if (!isMounted) return;

          setError('Failed to initialize payment form: ' + initError.message);
        }
        
      } else {
        if (!isMounted) return;


        setError('Payment provider not available. Please contact support.');
      }
      
    } catch (error) {
      if (!isMounted) return;

      setError('Failed to initialize payment form. Please refresh and try again.');
    }
    };

    // Start initialization
    initializePaymentForm();

    // Cleanup function
    return () => {
      isMounted = false;
      
      if (initTimeout) {
        clearTimeout(initTimeout);
      }
      
      if (connectERef.current) {
        // Disconnect the MutationObserver if it exists
        if (connectERef.current._observer) {
          connectERef.current._observer.disconnect();
          console.log('🧹 [PaymentSense] DOM observer disconnected');
        }

        // Clear the container to prevent duplicate forms
        const container = document.getElementById('payment-form-container');
        if (container) {
          container.innerHTML = '';
        }
        
        connectERef.current = null;
      }
    };
  }, [isScriptLoaded, paymentSession?.paymentSenseJWT, paymentSession?.paymentSenseCheckoutUrl, retryKey]);

  if (error) {
    return (
      <ErrorContainer>
        <ErrorTitle>
          <span>⚠️</span>
          Payment Error
        </ErrorTitle>
        <ErrorMessage>{error}</ErrorMessage>
        <RetryButton onClick={handleRetryPayment}>
          🔄 Try Again
        </RetryButton>
      </ErrorContainer>
    );
  }

  if (!isScriptLoaded) {
    return (
      <LoadingContainer>
        <div className="spinner"></div>
        <div>Loading secure payment form...</div>
        <div style={{ fontSize: '0.9rem', marginTop: '0.5rem' }}>
          Powered by Payment Sense
        </div>
      </LoadingContainer>
    );
  }

  return (
    <PaymentContainer>
      <div style={{
        marginBottom: '1.25rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        color: '#2c3e50'
      }}>
        <span style={{ fontSize: '1.1rem' }}>🔒</span>
        <div style={{ fontWeight: 600, fontSize: '1rem' }}>Secure Payment</div>
        <div style={{ marginLeft: 'auto', fontSize: '0.85rem', opacity: 0.75 }}>Powered by Payment Sense</div>
      </div>
      
      {/* Container for payment gateway form */}
      <div
        key={`payment-form-${retryKey}`}
        id="payment-form-container"
        ref={paymentFormRef}
        style={{
          width: '100%',
          maxWidth: '100%',
          clear: 'both'
        }}
      ></div>
      
      <style>{`
        /* 3D Secure iframe styling - CRITICAL for authentication challenges to display */
        iframe[name*="threeds"],
        iframe[name*="ThreeDS"],
        iframe[name*="3ds"],
        iframe[id*="threeds"],
        iframe[id*="ThreeDS"],
        iframe[id*="3ds"],
        div[id*="threeds"],
        div[id*="ThreeDS"],
        div[id*="3ds"] {
          position: fixed !important;
          top: 50% !important;
          left: 50% !important;
          transform: translate(-50%, -50%) !important;
          width: 600px !important;
          height: 600px !important;
          max-width: 95vw !important;
          max-height: 95vh !important;
          z-index: 999999 !important;
          background: white !important;
          border: none !important;
          box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.8) !important;
          border-radius: 12px !important;
        }
        
        #payment-form-container {
          width: 100% !important;
          max-width: 100% !important;
          margin: 0 !important;
          padding: 1rem !important;
          background: #ffffff !important;
          border-radius: 16px !important;
          border: 1px solid #e9ecef !important;
          box-shadow: 0 8px 24px rgba(0,0,0,0.06) !important;
        }
        #payment-form-container > div {
          width: 100% !important;
          max-width: 100% !important;
          min-width: 100% !important;
        }
        #payment-form-container iframe {
          width: 100% !important;
          max-width: 100% !important;
          min-width: 100% !important;
          min-height: 200px !important;
        }
        #payment-form-container input {
          min-width: 0 !important;
          width: 100% !important;
          box-sizing: border-box !important;
          font-size: 16px !important; /* Prevent iOS zoom on focus */
        }
        #payment-form-container::after {
          content: "";
          display: table;
          clear: both;
        }
        
        /* Target the Connect-E form elements more aggressively */
        #payment-form-container div[style*="width"],
        #payment-form-container div[style*="float"] {
          width: auto !important;
          min-width: auto !important;
        }
        
        /* Force the card number field to full width */
        #payment-form-container > div > div:first-child {
          width: 100% !important;
          margin-bottom: 1rem !important;
        }
        
        /* Force the expiry/CVV row to use flexbox */
        #payment-form-container > div > div {
          display: flex !important;
          flex-wrap: nowrap !important;
          gap: 1rem !important;
          width: 100% !important;
          margin-bottom: 1rem !important;
        }
        
        /* Card number field should be full width on its own row */
        #payment-form-container > div > div:first-child {
          flex-direction: column !important;
          gap: 0 !important;
        }
        
        /* Expiry and CVV fields side by side */
        #payment-form-container > div > div:last-child > div:first-child {
          flex: 1.7 !important;
          width: auto !important;
          float: none !important;
        }
        
        #payment-form-container > div > div:last-child > div:last-child {
          flex: 1 !important;
          width: auto !important;
          float: none !important;
          margin-left: 0 !important;
        }
        
        /* Mobile responsive styling */
        @media (max-width: 768px) {
          #payment-form-container {
            padding: 1rem !important;
            border-radius: 12px !important;
            margin: 0 -0.5rem !important; /* Extend to edges on mobile */
          }
          
          #payment-form-container > div > div:last-child {
            flex-direction: column !important;
            gap: 1rem !important;
          }
          
          #payment-form-container > div > div:last-child > div:first-child,
          #payment-form-container > div > div:last-child > div:last-child {
            flex: 1 !important;
            width: 100% !important;
            float: none !important;
            margin-left: 0 !important;
          }
          
          #payment-form-container input {
            padding: 0.875rem 1rem !important;
            min-height: 48px !important; /* Touch-friendly height */
          }
        }
        
        @media (max-width: 480px) {
          #payment-form-container {
            padding: 0.875rem !important;
            margin: 0 -0.75rem !important;
          }
        }

        /* Payment processing animations */
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @keyframes progress {
          0% { transform: translateX(-100%); }
          50% { transform: translateX(0%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
      
      {/* Processing payment loading state */}
      {processingPayment && (
        <div style={{
          background: 'linear-gradient(135deg, #e3f2fd 0%, #f8f9fa 100%)',
          border: '1px solid #bbdefb',
          borderRadius: '12px',
          padding: '1.5rem',
          marginBottom: '1.5rem',
          textAlign: 'center',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '1rem',
            marginBottom: '0.5rem'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '4px solid #e3f2fd',
              borderTop: '4px solid #2196f3',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            <div>
              <div style={{ fontSize: '1.1rem', fontWeight: '600', color: '#1976d2' }}>
                🔒 Processing Payment
              </div>
              <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.25rem' }}>
                Please wait while we securely process your payment...
              </div>
            </div>
          </div>
          <div style={{
            width: '100%',
            height: '4px',
            background: '#e0e0e0',
            borderRadius: '2px',
            overflow: 'hidden',
            marginTop: '1rem'
          }}>
            <div style={{
              height: '100%',
              background: 'linear-gradient(90deg, #2196f3 0%, #21cbf3 100%)',
              borderRadius: '2px',
              width: '100%',
              animation: 'progress 2s ease-in-out infinite'
            }}></div>
          </div>
        </div>
      )}

      <div style={{
        display: 'flex',
        gap: '1rem',
        marginTop: '2rem',
        justifyContent: 'space-between',
        flexWrap: 'wrap'
      }}>
        <button
          type="button"
          onClick={async () => {
            if (processingPayment) return; // Prevent double-clicks

            // Show both local and parent processing states
            setProcessingPayment(true);
            if (onPaymentProcessing) onPaymentProcessing(true);
            setError(null); // Clear any previous errors

            let iframeChecker = null; // Declare outside try block so we can clean up in catch

            try {
              if (!connectERef.current) {
                throw new Error('Payment form not initialized');
              }

              // Optional pre-validation - TEMPORARILY DISABLED for 3DS testing
              // This allows us to test 3DS flow even with test cards that fail validation
              try {
                // await connectERef.current.validate(); // Commented out for testing
                console.log('⚠️ Pre-validation skipped - testing 3DS flow');
              } catch (e) {
                setError('Please check your card details and try again.');
                setProcessingPayment(false);
                if (onPaymentProcessing) onPaymentProcessing(false);
                return;
              }

              // Execute payment - this may trigger 3DS challenge
              // Processing overlay is showing in background (z-index 9,999)
              // If 3DS is required, it pops up on top (z-index 999,999)
              // User completes 3DS, then overlay continues showing while order is created
              console.log('💳 [PaymentSense] Executing payment...');
              
              // Set up aggressive iframe monitoring during payment execution
              let iframeCheckCount = 0;
              iframeChecker = setInterval(() => {
                iframeCheckCount++;
                const allIframes = document.querySelectorAll('iframe');
                console.log(`🔍 [PaymentSense] Iframe check #${iframeCheckCount}: Found ${allIframes.length} iframes`);
                
                allIframes.forEach((iframe, idx) => {
                  const name = (iframe.name || '').toLowerCase();
                  const id = (iframe.id || '').toLowerCase();
                  const src = (iframe.src || '').toLowerCase();
                  
                  console.log(`  iframe[${idx}]:`, {
                    name: iframe.name,
                    id: iframe.id,
                    src: iframe.src.substring(0, 100) + (iframe.src.length > 100 ? '...' : ''),
                    display: iframe.style.display,
                    visibility: iframe.style.visibility,
                    position: iframe.style.position,
                    zIndex: iframe.style.zIndex
                  });
                  
                  // Check if this looks like a 3DS iframe
                  if (name.includes('threeds') || name.includes('3ds') || name.includes('cardinal') ||
                      id.includes('threeds') || id.includes('3ds') || id.includes('cardinal') ||
                      src.includes('cardinalcommerce') || src.includes('3ds') || src.includes('threeds')) {
                    
                    console.log('🎯 [PaymentSense] 3DS iframe detected during check!', { name, id, src: iframe.src });
                    
                    // Force it to be visible and centered
                    iframe.style.setProperty('position', 'fixed', 'important');
                    iframe.style.setProperty('top', '50%', 'important');
                    iframe.style.setProperty('left', '50%', 'important');
                    iframe.style.setProperty('transform', 'translate(-50%, -50%)', 'important');
                    iframe.style.setProperty('width', '600px', 'important');
                    iframe.style.setProperty('height', '600px', 'important');
                    iframe.style.setProperty('max-width', '95vw', 'important');
                    iframe.style.setProperty('max-height', '95vh', 'important');
                    iframe.style.setProperty('z-index', '999999', 'important');
                    iframe.style.setProperty('background', 'white', 'important');
                    iframe.style.setProperty('border', 'none', 'important');
                    iframe.style.setProperty('box-shadow', '0 0 0 9999px rgba(0, 0, 0, 0.8)', 'important');
                    iframe.style.setProperty('border-radius', '12px', 'important');
                    iframe.style.setProperty('display', 'block', 'important');
                    iframe.style.setProperty('visibility', 'visible', 'important');
                    iframe.style.setProperty('opacity', '1', 'important');
                    
                    console.log('✅ [PaymentSense] 3DS iframe forced visible!');
                  }
                });
                
                // Stop after 60 checks (30 seconds)
                if (iframeCheckCount >= 60) {
                  console.log('⏱️ [PaymentSense] Stopping iframe checker after 60 attempts');
                  clearInterval(iframeChecker);
                }
              }, 500); // Check every 500ms
              
              const result = await connectERef.current.executePayment();
              clearInterval(iframeChecker); // Stop checking once payment completes
              console.log('💳 [PaymentSense] Payment completed');

              if (result && (result.statusCode === 0 || result.status === 'Success')) {

                // Extract the payment token from the result - this is what Venpos needs
                const paymentToken = result.paymentToken || result.id || result.accessToken || paymentSession.paymentSenseJWT;

                // Processing overlay is already showing (started when user clicked Pay)
                // If 3DS was triggered, it appeared on top and user completed it
                // Now continue with the existing overlay showing while order is created
                
                // Call success callback - PaymentSense has already completed the payment
                // The parent will keep showing "Processing Your Booking" while creating the order
                onPaymentSuccess({
                  transactionId: result.transactionId || result.crossReference,
                  authCode: result.authCode || result.authCode,
                  amount: paymentSession?.amount,
                  currency: paymentSession?.currency || 'GBP',
                  status: 'Success',
                  paymentToken: paymentToken,
                  orderId: paymentSession?.orderId, // ✅ Include orderId from payment session
                  paymentResult: result // Include full result for debugging
                });
                
                return; // Exit early - don't run finally block
              } else {

                // Provide user-friendly error messages based on status code and message
                let userFriendlyMessage = result?.message || 'Payment declined';
                
                // Common error scenarios
                if (result?.message?.toLowerCase().includes('invalid data')) {
                  userFriendlyMessage = 'Please check your card details and try again. Make sure all fields are filled correctly.';
                } else if (result?.message?.toLowerCase().includes('declined')) {
                  userFriendlyMessage = 'Your card was declined. Please check your card details or try a different payment method.';
                } else if (result?.message?.toLowerCase().includes('insufficient')) {
                  userFriendlyMessage = 'Insufficient funds. Please use a different card or payment method.';
                } else if (result?.message?.toLowerCase().includes('expired')) {
                  userFriendlyMessage = 'Your card has expired. Please use a different payment method.';
                } else if (result?.message?.toLowerCase().includes('cvc') || result?.message?.toLowerCase().includes('cvv')) {
                  userFriendlyMessage = 'The security code (CVV/CVC) is incorrect. Please check the 3-digit code on the back of your card.';
                } else if (result?.statusCode === 5) {
                  userFriendlyMessage = 'Payment validation failed. Please check all card details are entered correctly.';
                } else if (result?.message?.toLowerCase().includes('3d secure') || result?.message?.toLowerCase().includes('authentication')) {
                  userFriendlyMessage = 'Bank authentication failed. Your bank may have declined the verification. Please try again or contact your bank.';
                } else if (result?.message?.toLowerCase().includes('timeout') || result?.message?.toLowerCase().includes('polling')) {
                  userFriendlyMessage = 'Payment verification timed out. Please try again or use a different card.';
                }
                
                setError(userFriendlyMessage);
                
                onPaymentFailure({
                  error: userFriendlyMessage,
                  code: result?.statusCode,
                  details: result
                });
                
                // Turn off processing overlay on failure
                setProcessingPayment(false);
                if (onPaymentProcessing) onPaymentProcessing(false);
              }
            } catch (err) {
              // Clean up iframe checker if it's still running
              if (iframeChecker) {
                clearInterval(iframeChecker);
                console.log('🧹 [PaymentSense] Cleaned up iframe checker after error');
              }

              const userError = 'An error occurred while processing your payment. Please try again.';
              setError(userError);
              onPaymentFailure({ error: userError, details: err });
              
              // Turn off processing overlay on error
              setProcessingPayment(false);
              if (onPaymentProcessing) onPaymentProcessing(false);
            }
          }}
          disabled={!isPaymentFormReady || processingPayment}
          style={{
            background: (!isPaymentFormReady || processingPayment)
              ? '#6c757d'
              : 'linear-gradient(135deg, #007bff 0%, #0056b3 100%)',
            color: '#fff',
            border: 'none',
            padding: '1rem 2rem',
            borderRadius: '0.5rem',
            cursor: (isPaymentFormReady && !processingPayment) ? 'pointer' : 'not-allowed',
            fontSize: '1rem',
            fontWeight: '600',
            boxShadow: (isPaymentFormReady && !processingPayment)
              ? '0 4px 12px rgba(0, 123, 255, 0.3)'
              : 'none',
            transition: 'all 0.2s ease-in-out',
            flex: '1 1 auto',
            minWidth: '160px',
            minHeight: '48px',
            position: 'relative'
          }}
        >
          {processingPayment ? (
            <>
              <span style={{
                display: 'inline-block',
                animation: 'spin 1s linear infinite',
                marginRight: '0.5rem'
              }}>⏳</span>
              Processing...
            </>
          ) : (
            <>
              💳 Pay £{Number(paymentSession?.amount || 0).toFixed(2)}
            </>
          )}
        </button>
        <button
          type="button"
          onClick={onPaymentCancel}
          disabled={processingPayment}
          style={{
            background: 'transparent',
            color: processingPayment ? '#adb5bd' : '#6c757d',
            border: `2px solid ${processingPayment ? '#dee2e6' : '#e9ecef'}`,
            padding: '1rem 1.5rem',
            borderRadius: '0.5rem',
            cursor: processingPayment ? 'not-allowed' : 'pointer',
            fontSize: '1rem',
            fontWeight: '500',
            transition: 'all 0.2s ease-in-out',
            flex: '0 1 auto',
            minWidth: '120px',
            minHeight: '48px'
          }}
          onMouseEnter={(e) => {
            if (!processingPayment) {
              e.target.style.borderColor = '#dc3545';
              e.target.style.color = '#dc3545';
            }
          }}
          onMouseLeave={(e) => {
            if (!processingPayment) {
              e.target.style.borderColor = '#e9ecef';
              e.target.style.color = '#6c757d';
            }
          }}
        >
          Cancel
        </button>
      </div>
      
      <div style={{
        marginTop: '1rem',
        fontSize: '0.75rem',
        color: '#8a8f98',
        textAlign: 'center'
      }}>
        SSL Encrypted • PCI DSS • Total Amount: £{paymentSession?.amount}
      </div>
    </PaymentContainer>
  );
};

export default memo(PaymentSenseConnectE);